import React from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
  Box, 
  Typography, 
  Button,
  Card, 
  CardContent,
  CardActions,
  useTheme,
  Chip,
  CardMedia,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  School as SchoolIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Computer as ComputerIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  ReadMore as ReadMoreIcon
} from '@mui/icons-material';
import { getCourseImageUrl } from '../../../utils/imageUtils';
import ImageWithSkeleton from '../../../Components/ImageWithSkeleton';
import circleContainer from '../../../assets/images/circle-container.svg';

const EnhancedCourseCard = ({ course, userId, onDelete }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleEdit = () => {
    navigate(`/course/edit/${course._id}`);
  };

  const handleView = () => {
    navigate(`/course/${course._id}`);
  };

  const handleReadMore = () => {
    navigate(`/course-detail/${course._id}`);
  };

  const handleDelete = async (e) => {
    e?.stopPropagation();

    // Show confirmation popup before deleting
    const result = await Swal.fire({
      title: "Are you sure?",
      html: `
        <div style="display: flex; flex-direction: column; align-items: center; padding: 20px 0;">
          <div style="
            width: 80px;
            height: 80px;
            border-radius: 50%;
            background: linear-gradient(135deg, rgba(113, 45, 148, 0.1), rgba(113, 45, 148, 0.2));
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 20px;
          ">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="#712d94" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10 11V17M14 11V17" stroke="#712d94" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <p style="font-size: 16px; color: #333; margin: 0; text-align: center;">
            This action will delete the course permanently!
          </p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#712d94",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      customClass: {
        popup: 'swal2-popup-custom',
        title: 'swal2-title-custom',
        confirmButton: 'swal2-confirm-custom',
        cancelButton: 'swal2-cancel-custom',
      },
      buttonsStyling: true,
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(
          `${process.env.REACT_APP_SERVER}/api/courses/delete/${course._id}`
        );

        if (response.status === 200) {
          Swal.fire({
            title: "Deleted!",
            text: "The course has been deleted.",
            icon: "success",
            confirmButtonColor: "#712d94",
          });
          // Call the refresh callback
          if (onDelete) {
            onDelete();
          }
        } else {
          Swal.fire({
            title: "Error!",
            text: "Failed to delete the course. Try again.",
            icon: "error",
            confirmButtonColor: "#712d94",
          });
        }
      } catch (error) {
        console.error("Error while deleting course:", error);
        Swal.fire({
          title: "Error!",
          text: "Something went wrong. Please try again.",
          icon: "error",
          confirmButtonColor: "#712d94",
        });
      }
    }
  };

  // Format course data
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };


  const getStatusColor = (course) => {
    if (course.isDeleted) return 'error';
    if (course.isExpire) return 'warning';
    if (course.isDisabled) return 'warning';
    if (course.isApproved) return 'success';
    return 'info'; // Pending approval
  };

  const getStatusLabel = (course) => {
    if (course.isDeleted) return 'Deleted';
    if (course.isExpire) return 'Expired';
    if (course.isDisabled) return 'Disabled';
    if (course.isApproved) return 'Live';
    return 'Pending Review';
  };

  // Parse course types - handle both string and array formats
  const courseTypes = course.types ? 
    (Array.isArray(course.types) ? course.types : course.types.split(',')) : [];
  const contactSources = course.contactSources ? 
    (Array.isArray(course.contactSources) ? course.contactSources : course.contactSources.split(',')) : [];

  return (
    <Card sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 2,
      overflow: 'hidden',
      background: '#ffffff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      },
      transition: 'box-shadow 0.2s ease',
    }}>
      {/* Course Image with overlayed status */}
      <Box sx={{ position: 'relative' }}>
        <ImageWithSkeleton
          src={getCourseImageUrl(course.image)}
          alt={course.name || 'Course image'}
          height={180}
          onErrorSrc={'/assets/course-place1.png'}
          sx={{ borderTopLeftRadius: 'inherit', borderTopRightRadius: 'inherit', objectFit: 'cover' }}
        />
        <Chip 
          label={getStatusLabel(course)} 
          color={getStatusColor(course)}
          size="small"
          sx={{ 
            position: 'absolute',
            top: 10,
            right: 10,
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 24,
            borderRadius: 1.5,
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            '& .MuiChip-label': { px: 1.25 }
          }}
        />
      </Box>
      
      <CardContent sx={{ flexGrow: 1, p: 2.5, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                mb: 0.5,
                fontSize: '1.1rem',
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {course.name || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
              {course.subject} • {course.level}
            </Typography>
          </Box>
          {/* Compact price badge */}
          <Box sx={{ position: 'relative', width: 56, height: 56, ml: 1, flexShrink: 0 }}>
            <img 
              src={circleContainer} 
              alt="Price Circle" 
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              lineHeight: 1.1
            }}>
              <Typography variant="body2" sx={{ fontWeight: 800, color: '#712d94', fontSize: '0.8rem' }}>
                {course.coursePrice && course.coursePrice !== '0' && course.coursePrice !== 0 ? `£${course.coursePrice}` : 'Free'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#712d94', fontSize: '0.5rem' }}>Price</Typography>
            </Box>
          </Box>
        </Box>

        {/* Course Details - Compact */}
        <Box sx={{ mb: 1.5, flex: 1 }}>
          <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500, fontSize: '0.8125rem' }}>
            <strong>Subject:</strong> {course.subject || 'N/A'}
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500, fontSize: '0.8125rem' }}>
            <strong>Level:</strong> {course.level || 'N/A'}
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500, fontSize: '0.8125rem' }}>
            <strong>Location:</strong> {courseTypes.length > 0 ? courseTypes.join(' / ') : 'N/A'}
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500, fontSize: '0.8125rem' }}>
            <strong>Date:</strong> {course.isOngoing ? 'Anytime' : `${formatDate(course.date?.[0])} - ${formatDate(course.date?.[1])}`}
          </Typography>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 'auto', pt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Created: {formatDate(course.createdAt)}
          </Typography>
        </Box>
      </CardContent>

      <CardActions sx={{ p: 2, pt: 1, gap: 1.25, borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'stretch' }}>
        <Button
          variant="outlined"
          startIcon={<ReadMoreIcon />}
          onClick={handleReadMore}
          sx={{
            flex: 1,
            minWidth: 0,
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            borderRadius: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            py: 0.9,
            px: 1.5,
            fontSize: '0.875rem',
            whiteSpace: 'nowrap',
            '& .MuiButton-startIcon': {
              marginRight: '6px',
            },
            '&:hover': {
              borderColor: theme.palette.primary.dark,
              backgroundColor: theme.palette.primary.main + '08',
            }
          }}
        >
          Read More
        </Button>
        {userId && String(userId) === String(course?.userId) && (
          <>
            <Tooltip title="Delete course" arrow>
              <IconButton
                onClick={handleDelete}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  backgroundColor: '#d32f2f',
                  color: '#fff',
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: '#b71c1c',
                  }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit course" arrow>
              <IconButton
                onClick={handleEdit}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  backgroundColor: theme.palette.primary.main,
                  color: '#fff',
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  }
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </CardActions>
    </Card>
  );
};

export default EnhancedCourseCard;
